import type {
  YachtCategory,
  YachtScorePreview,
  YachtViewerState,
} from "@cloudnest/yacht-engine";
import {
  previewScoresForDice,
  toScoringDice,
  YACHT_CATEGORIES,
  YACHT_UPPER_BONUS_POINTS,
  YACHT_UPPER_BONUS_THRESHOLD,
} from "@cloudnest/yacht-engine";
import { Fragment } from "react";
import { YACHT_CATEGORY_LABELS } from "./ScoreSheetGrid";

function getPreview(
  previews: readonly YachtScorePreview[],
  category: YachtCategory,
): YachtScorePreview | null {
  return previews.find((preview) => preview.category === category) ?? null;
}

function getPlayerName(state: YachtViewerState, playerId: string): string {
  return (
    state.players.find((player) => player.playerId === playerId)?.displayName ??
    playerId
  );
}

function getCurrentPlayerId(state: YachtViewerState): string | null {
  return (
    state.seatingOrder.find(
      (playerId) => state.turnSlotsByPlayerId[playerId]?.status === "OPEN",
    ) ?? null
  );
}

function bonusParts(
  state: YachtViewerState,
  playerId: string,
): { readonly primary: string; readonly secondary: string } {
  const sheet = state.scoreSheetsByPlayerId[playerId];
  const subtotal = sheet?.upperSubtotal ?? 0;
  const needed = Math.max(0, YACHT_UPPER_BONUS_THRESHOLD - subtotal);
  if (sheet?.upperBonus === YACHT_UPPER_BONUS_POINTS) {
    return {
      primary: `${subtotal}/${YACHT_UPPER_BONUS_THRESHOLD}`,
      secondary: `(+${YACHT_UPPER_BONUS_POINTS})`,
    };
  }
  return {
    primary: `${subtotal}/${YACHT_UPPER_BONUS_THRESHOLD}`,
    secondary: `(${needed})`,
  };
}

function getOpenCategoryPreviews(
  state: YachtViewerState,
  playerId: string,
): readonly YachtScorePreview[] {
  const slot = state.turnSlotsByPlayerId[playerId];
  if (!slot || slot.status !== "OPEN" || slot.rollCount === 0) return [];

  const dice = toScoringDice(slot.dice);
  if (!dice) return [];

  const sheet = state.scoreSheetsByPlayerId[playerId];
  const openCategories = YACHT_CATEGORIES.filter(
    (category) => sheet?.entries[category] === undefined,
  );
  return previewScoresForDice(openCategories, dice);
}

export function ScoreBoardMatrix({
  state,
  selectedCategory,
  onSelectCategory,
  onOpenPlayerDetail,
}: {
  readonly state: YachtViewerState;
  readonly selectedCategory: YachtCategory | null;
  readonly onSelectCategory: (category: YachtCategory) => void;
  readonly onOpenPlayerDetail: (playerId: string) => void;
}) {
  const viewerPlayerId = state.viewerPlayerId;
  const currentPlayerId = getCurrentPlayerId(state);

  return (
    <section className="yacht-score-board" data-testid="yacht-score-board">
      <table className="yacht-score-board-table">
        <thead>
          <tr>
            <th>조합</th>
            {state.seatingOrder.map((playerId) => (
              <th
                data-current={playerId === currentPlayerId ? "true" : "false"}
                data-self={playerId === viewerPlayerId ? "true" : "false"}
                key={playerId}
              >
                <button
                  className="yacht-board-player-button"
                  onClick={() => onOpenPlayerDetail(playerId)}
                  title={getPlayerName(state, playerId)}
                  type="button"
                >
                  {getPlayerName(state, playerId)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {YACHT_CATEGORIES.map((category, index) => (
            <Fragment key={category}>
              {index === 6 && (
                <tr className="yacht-score-board-bonus" key="upper-bonus">
                  <td>보너스</td>
                  {state.seatingOrder.map((playerId) => {
                    const bonus = bonusParts(state, playerId);
                    return (
                      <td
                        data-current={
                          playerId === currentPlayerId ? "true" : "false"
                        }
                        data-self={
                          playerId === viewerPlayerId ? "true" : "false"
                        }
                        data-testid={`yacht-score-board-bonus-${playerId}`}
                        key={playerId}
                      >
                        <span className="yacht-board-bonus">
                          <span>{bonus.primary}</span>
                          <span>{bonus.secondary}</span>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              )}
              <tr className="yacht-score-board-row">
                <td>{YACHT_CATEGORY_LABELS[category]}</td>
                {state.seatingOrder.map((playerId) => {
                  const sheet = state.scoreSheetsByPlayerId[playerId];
                  const entry = sheet?.entries[category];
                  const previews =
                    playerId === currentPlayerId
                      ? getOpenCategoryPreviews(state, playerId)
                      : [];
                  const preview = getPreview(previews, category);
                  const selected =
                    playerId === viewerPlayerId &&
                    selectedCategory === category &&
                    preview !== null;
                  const cellState =
                    entry !== undefined
                      ? "confirmed"
                      : selected
                        ? "pending"
                        : preview !== null
                          ? "available"
                          : "empty";
                  const score =
                    entry?.score ?? (selected ? preview?.score : "");

                  return (
                    <td
                      data-current={
                        playerId === currentPlayerId ? "true" : "false"
                      }
                      data-recommended={preview?.recommended ? "true" : "false"}
                      data-self={playerId === viewerPlayerId ? "true" : "false"}
                      data-state={cellState}
                      data-testid={`yacht-score-board-${playerId}-${category.toLowerCase()}`}
                      key={playerId}
                      onClick={() => {
                        if (preview !== null && playerId === viewerPlayerId) {
                          onSelectCategory(category);
                        }
                      }}
                    >
                      <span>{score}</span>
                      {preview?.recommended && (
                        <span
                          aria-hidden="true"
                          className="yacht-board-recommendation-mark"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            </Fragment>
          ))}
          <tr className="yacht-score-board-total">
            <td>총합</td>
            {state.seatingOrder.map((playerId) => (
              <td
                data-current={playerId === currentPlayerId ? "true" : "false"}
                data-self={playerId === viewerPlayerId ? "true" : "false"}
                key={playerId}
              >
                {state.scoreSheetsByPlayerId[playerId]?.total ?? 0}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </section>
  );
}
